import { useState, useEffect, useMemo } from 'react'
import { Group, SegmentedControl, Select, Skeleton, Stack, Text } from '@mantine/core'
import { AreaChart } from '@mantine/charts'
import { IconChartArea, IconDatabaseOff } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useCurrentTenant } from '@/store/auth'
import { databaseApi } from '@/api/database'
import { WidgetCard, CardHeader, EmptyState } from '@/components/ui'
import { EstadoDeConsulta } from '@/components/ui/EstadoDeConsulta'

const PERIODS = [
  { value: '1', label: '24h' },
  { value: '7', label: '7d' },
  { value: '14', label: '14d' },
  { value: '30', label: '30d' },
]

const METRICS: { key: string; title: string; unit: string; color: string; threshold?: number }[] = [
  { key: 'cpu_avg_pct', title: 'CPU Utilization', unit: '%', color: 'orange.5', threshold: 80 },
  { key: 'connections_used_pct', title: 'Connections Usage', unit: '%', color: 'blue.5', threshold: 85 },
  { key: 'storage_used_pct', title: 'Storage Usage', unit: '%', color: 'red.5', threshold: 90 },
  { key: 'read_latency_ms', title: 'Read Latency', unit: 'ms', color: 'teal.5' },
]

function MetricChart({
  title,
  metricKey,
  unit,
  color,
  threshold,
  tenantId,
  dbId,
  days,
}: {
  title: string
  metricKey: string
  unit: string
  color: string
  threshold?: number
  tenantId: number
  dbId: string
  days: number
}) {
  const consulta = useQuery({
    queryKey: ['metrics-history', tenantId, dbId, metricKey, days],
    queryFn: () => databaseApi.getMetricsHistory(tenantId, dbId, metricKey, days),
    enabled: !!tenantId && !!dbId,
    staleTime: 60_000,
  })
  const data = consulta.data

  const chartData = useMemo(() => {
    const points = data?.points
    if (!points?.length) return []
    return points.map((p) => ({
      ts: new Date(p.ts).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
      value: Math.round(p.value * 100) / 100,
    }))
  }, [data])

  const latest = chartData.length > 0 ? chartData[chartData.length - 1].value : null
  const avg = chartData.length > 0 ? Math.round(chartData.reduce((s, p) => s + p.value, 0) / chartData.length * 100) / 100 : null
  const max = chartData.length > 0 ? Math.max(...chartData.map((p) => p.value)) : null

  return (
    <WidgetCard>
      <CardHeader title={title} />
      <Stack gap="sm" p="md">
        {/* Antes: falha de rede caia no mesmo ramo do vazio, e o grafico mandava "tentar um
          periodo maior" — conselho inutil, porque o periodo nao era o problema. */}
        <EstadoDeConsulta
          consulta={consulta}
          carregando={<Skeleton height={200} />}
          vazio={chartData.length === 0}
          estadoVazio={
            <EmptyState
              bare
              size="sm"
              accent="info"
              icon={IconChartArea}
              title="Sem dados para este período"
              description="Tente um período maior ou aguarde a próxima coleta de métricas."
            />
          }
        >
          <>
            <Group gap="lg">
              <Group gap={4}>
                <Text size="xs" c="dimmed">Atual:</Text>
                <Text size="xs" fw={600}>{latest}{unit}</Text>
              </Group>
              <Group gap={4}>
                <Text size="xs" c="dimmed">Média:</Text>
                <Text size="xs" fw={600}>{avg}{unit}</Text>
              </Group>
              <Group gap={4}>
                <Text size="xs" c="dimmed">Max:</Text>
                <Text size="xs" fw={600}>{max}{unit}</Text>
              </Group>
            </Group>
            <AreaChart
              h={200}
              data={chartData}
              dataKey="ts"
              series={[{ name: 'value', color, label: title }]}
              curveType="monotone"
              withDots={false}
              withTooltip
              tooltipAnimationDuration={200}
              referenceLines={threshold ? [{ y: threshold, color: 'red.5', label: `Limite ${threshold}${unit}` }] : undefined}
            />
          </>
        </EstadoDeConsulta>
      </Stack>
    </WidgetCard>
  )
}

export function MetricsTab() {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  const [selectedDb, setSelectedDb] = useState<string | null>(null)
  const [days, setDays] = useState('7')

  const instancesQuery = useQuery({
    queryKey: ['rds-instances', tenantId],
    queryFn: () => databaseApi.listInstances(tenantId!),
    enabled: !!tenantId,
    staleTime: 60_000,
  })

  useEffect(() => {
    if (instancesQuery.data?.items?.length && !selectedDb) {
      setSelectedDb(instancesQuery.data.items[0].db_identifier)
    }
  }, [instancesQuery.data, selectedDb])

  if (!tenantId) return <Text c="dimmed">Selecione um tenant</Text>

  const instances = instancesQuery.data?.items ?? []

  return (
    <Stack gap="md">
      {/* Mesma armadilha do OverviewTab: sem instancia escolhida, `selectedDb &&` apaga o corpo
        inteiro da aba. Com a listagem fora do ar a tela ficava em branco, calada. */}
      <EstadoDeConsulta
        consulta={instancesQuery}
        carregando={<Skeleton width={350} height={60} />}
        vazio={instances.length === 0}
        estadoVazio={
          <EmptyState
            size="md"
            accent="primary"
            icon={IconDatabaseOff}
            title="Nenhuma instância encontrada"
            description="Configure o acesso AWS do tenant para acompanhar o histórico de métricas."
          />
        }
      >
      <Group justify="space-between" align="flex-end">
        <Select
          label="Instância RDS"
          data={(instancesQuery.data?.items ?? []).map((i) => ({
            value: i.db_identifier,
            label: `${i.db_identifier} (${i.engine})`,
          }))}
          value={selectedDb}
          onChange={setSelectedDb}
          disabled={instancesQuery.isLoading}
          w={350}
        />
        <SegmentedControl
          size="xs"
          data={PERIODS}
          value={days}
          onChange={setDays}
        />
      </Group>

      {selectedDb && (
        <Stack gap="md">
          {METRICS.map((m) => (
            <MetricChart
              key={m.key}
              title={m.title}
              metricKey={m.key}
              unit={m.unit}
              color={m.color}
              threshold={m.threshold}
              tenantId={tenantId}
              dbId={selectedDb}
              days={Number(days)}
            />
          ))}
        </Stack>
      )}
      </EstadoDeConsulta>
    </Stack>
  )
}
